import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MapPin, Calendar, Plus, Search, Trash2, Eye, Route,
  Building2, ArrowLeft, CheckCircle, Clock, Users, Loader,
  Leaf, Navigation, ExternalLink, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getVersionString } from '../config/version';

const AdminToursPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');
  
  const [tours, setTours] = useState([]);
  const [salespeople, setSalespeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTour, setSelectedTour] = useState(null);
  
  // Create form state
  const [newTour, setNewTour] = useState({
    date: new Date().toISOString().split('T')[0],
    location: '',
    postal_code: '',
    radius_km: 20,
    notes: '',
    salesperson_id: ''
  });
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadTours();
    loadSalespeople();
  }, [token, navigate]);

  const loadSalespeople = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/salespeople`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      // Only show active salespeople
      setSalespeople(Array.isArray(data) ? data.filter(s => s.is_active) : []);
    } catch (error) {
      console.error('Load salespeople error:', error);
    }
  };

  const loadTours = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/sales-tours/tours`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      setTours(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Load tours error:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchPreview = async () => {
    if (!newTour.location) return;
    
    setPreviewLoading(true);
    setPreview(null);
    
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/sales-tours/search-preview?location=${encodeURIComponent(newTour.location)}&radius_km=${newTour.radius_km}`;
      if (newTour.postal_code) {
        url += `&postal_code=${encodeURIComponent(newTour.postal_code)}`;
      }
      
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      
      if (!response.ok) {
        const error = await response.json();
        alert(error.detail || 'Suche fehlgeschlagen');
        return;
      }
      
      const data = await response.json();
      setPreview(data);
    } catch (error) {
      console.error('Preview error:', error);
      alert('Suche fehlgeschlagen');
    } finally {
      setPreviewLoading(false);
    }
  };

  const createTour = async () => {
    if (!newTour.location || !newTour.date) {
      alert('Bitte Datum und Ort angeben');
      return;
    }
    
    setCreating(true);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/sales-tours/tours`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(newTour)
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        alert(error.detail || 'Erstellen fehlgeschlagen');
        return;
      }
      
      setShowCreateModal(false);
      setNewTour({ date: new Date().toISOString().split('T')[0], location: '', postal_code: '', radius_km: 20, notes: '', salesperson_id: '' });
      setPreview(null);
      loadTours();
    } catch (error) {
      console.error('Create error:', error);
      alert('Erstellen fehlgeschlagen');
    } finally {
      setCreating(false);
    }
  };

  const deleteTour = async (tourId) => {
    if (!confirm('Tour wirklich löschen?')) return;
    
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/sales-tours/tours/${tourId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      loadTours();
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      geplant: 'bg-blue-100 text-blue-700',
      aktiv: 'bg-green-100 text-green-700',
      abgeschlossen: 'bg-gray-100 text-gray-700'
    };
    const labels = {
      geplant: 'Geplant',
      aktiv: 'Aktiv',
      abgeschlossen: 'Abgeschlossen'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || styles.geplant}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="w-8 h-8 text-brand-green animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 md:px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 md:gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Zurück</span>
            </Button>
            <h1 className="text-lg md:text-xl font-bold text-gray-900">Vertrieb-Touren</h1>
          </div>
          <Button 
            onClick={() => setShowCreateModal(true)} 
            className="bg-brand-green hover:bg-brand-green-dark text-white w-full sm:w-auto"
            size="sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neue Tour planen
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-8">
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {tours.filter(t => t.status === 'geplant').length}
                </div>
                <div className="text-sm text-gray-600">Geplante Touren</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {tours.filter(t => t.status === 'abgeschlossen').length}
                </div>
                <div className="text-sm text-gray-600">Abgeschlossen</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {tours.reduce((sum, t) => sum + (t.business_count || 0), 0)}
                </div>
                <div className="text-sm text-gray-600">Betriebe gefunden</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tours List */}
        <div className="bg-white rounded-2xl border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900">Alle Touren</h2>
          </div>
          
          {tours.length === 0 ? (
            <div className="p-12 text-center">
              <Route className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Keine Touren</h3>
              <p className="text-gray-600 mb-4">Erstellen Sie die erste Vertrieb-Tour</p>
              <Button onClick={() => setShowCreateModal(true)} className="bg-brand-green hover:bg-brand-green-dark text-white">
                <Plus className="w-5 h-5 mr-2" />
                Tour erstellen
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {tours.map(tour => (
                <div key={tour.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-gray-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-semibold text-gray-900">{tour.location}</h3>
                          {getStatusBadge(tour.status)}
                        </div>
                        <div className="text-sm text-gray-600">
                          {new Date(tour.date).toLocaleDateString('de-DE', { 
                            weekday: 'long', 
                            day: 'numeric', 
                            month: 'long',
                            year: 'numeric'
                          })}
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {tour.business_count} Betriebe
                          </span>
                          <span className="flex items-center gap-1">
                            <Route className="w-4 h-4" />
                            {tour.total_distance_km} km
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {tour.radius_km} km Radius
                          </span>
                          {tour.route_optimized && (
                            <span className="flex items-center gap-1 text-green-600">
                              <Leaf className="w-4 h-4" />
                              Optimiert
                            </span>
                          )}
                          {tour.salesperson_id && (
                            <span className="flex items-center gap-1 text-blue-600">
                              <User className="w-4 h-4" />
                              {salespeople.find(s => s.id === tour.salesperson_id)?.first_name || 'Zugewiesen'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedTour(tour)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Details
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteTour(tour.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Neue Tour planen</h2>
              <Button variant="ghost" onClick={() => { setShowCreateModal(false); setPreview(null); }}>
                ×
              </Button>
            </div>
            
            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Form */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Datum *
                    </label>
                    <input
                      type="date"
                      value={newTour.date}
                      onChange={(e) => setNewTour({...newTour, date: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ort / Stadt *
                    </label>
                    <input
                      type="text"
                      placeholder="z.B. Cuxhaven"
                      value={newTour.location}
                      onChange={(e) => setNewTour({...newTour, location: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      PLZ (optional)
                    </label>
                    <input
                      type="text"
                      placeholder="z.B. 25462"
                      value={newTour.postal_code}
                      onChange={(e) => setNewTour({...newTour, postal_code: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                      maxLength={5}
                    />
                    <p className="text-xs text-gray-500 mt-1">Für genauere Ortsbestimmung</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Suchradius: {newTour.radius_km} km
                    </label>
                    <input
                      type="range"
                      min="5"
                      max="50"
                      step="5"
                      value={newTour.radius_km}
                      onChange={(e) => setNewTour({...newTour, radius_km: parseInt(e.target.value)})}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-brand-green"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>5 km</span>
                      <span>50 km</span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notizen
                    </label>
                    <textarea
                      placeholder="Optionale Notizen zur Tour..."
                      value={newTour.notes}
                      onChange={(e) => setNewTour({...newTour, notes: e.target.value})}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                    />
                  </div>
                  
                  {/* Salesperson Assignment */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <User className="w-4 h-4 inline mr-1" />
                      Vertriebler zuweisen
                    </label>
                    <select
                      value={newTour.salesperson_id}
                      onChange={(e) => setNewTour({...newTour, salesperson_id: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-brand-green bg-white"
                    >
                      <option value="">-- Nicht zugewiesen --</option>
                      {salespeople.map(sp => (
                        <option key={sp.id} value={sp.id}>
                          {sp.first_name} {sp.last_name}
                        </option>
                      ))}
                    </select>
                    {salespeople.length === 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Keine aktiven Vertriebler. <a href="/admin/vertriebsteam" className="text-brand-green hover:underline">Jetzt einladen</a>
                      </p>
                    )}
                  </div>
                  
                  <Button
                    onClick={searchPreview}
                    disabled={!newTour.location || previewLoading}
                    variant="outline"
                    className="w-full"
                  >
                    {previewLoading ? (
                      <Loader className="w-5 h-5 mr-2 animate-spin" />
                    ) : (
                      <Search className="w-5 h-5 mr-2" />
                    )}
                    Betriebe suchen
                  </Button>
                </div>
                
                {/* Preview */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Vorschau</h3>
                  
                  {!preview && !previewLoading && (
                    <div className="text-center py-8 text-gray-500">
                      <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-sm">Geben Sie einen Ort ein und klicken Sie auf "Betriebe suchen"</p>
                    </div>
                  )}
                  
                  {previewLoading && (
                    <div className="text-center py-8">
                      <Loader className="w-8 h-8 mx-auto mb-3 text-brand-green animate-spin" />
                      <p className="text-sm text-gray-600">Suche läuft...</p>
                    </div>
                  )}
                  
                  {preview && (
                    <div>
                      <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200">
                        <div className="grid grid-cols-2 gap-4 text-center">
                          <div>
                            <div className="text-2xl font-bold text-brand-green">{preview.business_count}</div>
                            <div className="text-xs text-gray-600">Betriebe gefunden</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-blue-600">{preview.total_distance_km} km</div>
                            <div className="text-xs text-gray-600">Gesamtstrecke</div>
                          </div>
                        </div>
                        
                        {/* Optimization Status */}
                        {preview.route_optimized && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="flex items-center justify-center gap-2 text-sm text-green-600">
                              <Leaf className="w-4 h-4" />
                              <span className="font-medium">Umweltschonend optimiert</span>
                            </div>
                            <p className="text-xs text-center text-gray-500 mt-1">
                              Route mit Google Routes API berechnet
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <div className="max-h-64 overflow-y-auto space-y-2">
                        {preview.businesses?.slice(0, 10).map((biz, idx) => (
                          <div key={biz.place_id} className="bg-white rounded-lg p-3 border border-gray-200 text-sm">
                            <div className="flex items-start gap-2">
                              <span className="w-6 h-6 bg-brand-green text-white rounded-full flex items-center justify-center text-xs font-bold">
                                {biz.route_order || idx + 1}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 truncate">{biz.name}</div>
                                <div className="text-xs text-gray-500 truncate">{biz.address}</div>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-brand-green">{biz.business_type}</span>
                                  {biz.distance_from_prev && (
                                    <span className="text-xs text-gray-400 flex items-center gap-1">
                                      <Navigation className="w-3 h-3" />
                                      {biz.distance_from_prev} km
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                        {preview.business_count > 10 && (
                          <div className="text-center text-sm text-gray-500 py-2">
                            + {preview.business_count - 10} weitere Betriebe
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setShowCreateModal(false); setPreview(null); }}>
                Abbrechen
              </Button>
              <Button
                onClick={createTour}
                disabled={!preview || creating}
                className="bg-brand-green hover:bg-brand-green-dark text-white"
              >
                {creating ? (
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-5 h-5 mr-2" />
                )}
                Tour erstellen
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tour Detail Modal */}
      {selectedTour && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{selectedTour.location}</h2>
                <p className="text-sm text-gray-600">
                  {new Date(selectedTour.date).toLocaleDateString('de-DE', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long' 
                  })}
                </p>
              </div>
              <Button variant="ghost" onClick={() => setSelectedTour(null)}>×</Button>
            </div>
            
            <div className="p-6">
              {/* Assigned Salesperson */}
              {selectedTour.salesperson_id && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-3">
                  <User className="w-5 h-5 text-blue-600" />
                  <div>
                    <div className="font-medium text-blue-800">
                      Zugewiesen an: {salespeople.find(s => s.id === selectedTour.salesperson_id)?.first_name} {salespeople.find(s => s.id === selectedTour.salesperson_id)?.last_name || 'Vertriebler'}
                    </div>
                    <div className="text-xs text-blue-600">Diese Tour erscheint im Dashboard des Vertrieblers</div>
                  </div>
                </div>
              )}
              
              {/* Optimization Badge */}
              {selectedTour.route_optimized && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center gap-3">
                  <Leaf className="w-5 h-5 text-green-600" />
                  <div>
                    <div className="font-medium text-green-800">Umweltschonend optimierte Route</div>
                    <div className="text-xs text-green-600">Berechnet mit Google Routes API für minimale Fahrstrecke</div>
                  </div>
                </div>
              )}
              
              {/* Map Links */}
              <div className="flex gap-3 mb-6">
                <Button
                  onClick={() => {
                    const businesses = selectedTour.businesses?.slice(0, 10) || [];
                    const waypoints = businesses.map(b => encodeURIComponent(b.address)).join('|');
                    const origin = encodeURIComponent(selectedTour.location_formatted);
                    const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${origin}&waypoints=${waypoints}&travelmode=driving`;
                    window.open(url, '_blank');
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Navigation className="w-5 h-5 mr-2" />
                  Google Maps öffnen
                </Button>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-gray-900">{selectedTour.business_count}</div>
                  <div className="text-xs text-gray-600">Betriebe</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-gray-900">{selectedTour.total_distance_km} km</div>
                  <div className="text-xs text-gray-600">Strecke</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-gray-900">{selectedTour.visited_businesses?.length || 0}</div>
                  <div className="text-xs text-gray-600">Besucht</div>
                </div>
              </div>
              
              {/* Business List */}
              <h3 className="font-semibold text-gray-900 mb-3">Betriebe ({selectedTour.business_count})</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {selectedTour.businesses?.map((biz, idx) => {
                  const visited = selectedTour.visited_businesses?.some(v => v.place_id === biz.place_id);
                  return (
                    <div 
                      key={biz.place_id} 
                      className={`p-4 rounded-lg border ${visited ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}
                    >
                      <div className="flex items-start gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          visited ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {visited ? <CheckCircle className="w-5 h-5" /> : idx + 1}
                        </span>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{biz.name}</div>
                          <div className="text-sm text-gray-600">{biz.address}</div>
                          <div className="flex items-center gap-3 mt-1 text-xs">
                            <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                              {biz.business_type}
                            </span>
                            {biz.rating && (
                              <span className="text-yellow-600">★ {biz.rating}</span>
                            )}
                            {biz.distance_from_prev && (
                              <span className="text-gray-500">{biz.distance_from_prev} km</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminToursPage;
